import React from 'react';
import {
  ShoppingBag,
  Stethoscope,
  Shield,
  Clock,
  Star
} from 'lucide-react';

/**
 * ملاحظة: لأفضل ظهور للنص العربي، أضف خط "Cairo" في index.html أو ملف CSS الرئيسي:
 * <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&display=swap" rel="stylesheet" />
 */

const HeroSection = ({ setCurrentPage }) => {
  const features = [
    { icon: <Shield className="w-5 h-5" />, text: 'منتجات أصلية 100%' },
    { icon: <Clock className="w-5 h-5" />, text: 'توصيل سريع خلال ساعتين' },
    { icon: <Stethoscope className="w-5 h-5" />, text: 'استشارة صيدلانية مجانية' }
  ];

  // "تسوق الآن" بينزل لقسم الفئات في نفس الصفحة الرئيسية
  const handleShopNowClick = () => {
    const categoriesSection = document.getElementById('categories');
    if (categoriesSection) {
      categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // "استشارة صيدلانية" بيودي لصفحة الاستشارة اللي فيها الفورم
  const handleConsultationClick = () => {
    setCurrentPage && setCurrentPage('consultation');
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#10B981]/[0.06] via-white to-indigo-500/[0.06] pb-8 pt-8 lg:pb-12 lg:pt-12 font-['Cairo']">
      {/* زخرفة خلفية هادئة بشكل كبسولة دواء */}
      <div className="pointer-events-none absolute -left-24 top-10 h-72 w-72 rounded-full bg-[#10B981]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-16 bottom-0 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />

      <div className="container relative mx-auto px-4">
        <div className="grid items-center gap-14 lg:grid-cols-2">
          {/* المحتوى النصي */}
          <div className="space-y-8">
            <div className="space-y-5">
              <div className="inline-flex items-center gap-1.5 rounded-full border border-[#10B981]/20 bg-[#10B981]/10 px-4 py-2 text-sm font-semibold text-[#10B981]">
                <Star className="h-4 w-4 fill-current" />
                الصيدلية الأولى في المملكة
              </div>

              <h1 className="text-4xl font-extrabold leading-[1.15] text-gray-900 lg:text-5xl">
                أدوية، فيتامينات ومستلزمات طبية،
                <span className="text-[#10B981]"> بأسعار لا تقبل المنافسة</span>
              </h1>
              <p className="max-w-xl text-lg leading-relaxed text-gray-600">
                اكتشف مجموعتنا الواسعة من المنتجات الصحية المصممة لتلبية جميع احتياجاتك
                بأفضل جودة وأسعار.
              </p>
            </div>

            <div className="flex flex-col gap-4 sm:flex-row">
              <button
                onClick={handleShopNowClick}
                className="group inline-flex items-center justify-center gap-2 rounded-xl bg-[#10B981] px-8 py-4 text-lg font-bold text-white shadow-md shadow-[#10B981]/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#0ea173] hover:shadow-xl hover:shadow-[#10B981]/30 active:translate-y-0"
              >
                <ShoppingBag className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
                تسوق الآن
              </button>
              <button
                onClick={handleConsultationClick}
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-gray-200 px-8 py-4 text-lg font-bold text-gray-700 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#10B981] hover:text-[#10B981]"
              >
                <Stethoscope className="h-5 w-5" />
                استشارة صيدلانية
              </button>
            </div>

            <div className="flex flex-wrap gap-4 pt-2">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-3 rounded-xl bg-white/70 px-4 py-3 shadow-sm ring-1 ring-gray-100 backdrop-blur-sm transition-all duration-300 hover:shadow-md hover:ring-[#10B981]/20"
                >
                  <div className="rounded-full bg-[#10B981]/10 p-2 text-[#10B981]">
                    {feature.icon}
                  </div>
                  <span className="text-sm font-semibold text-gray-700">{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* الصورة الرئيسية */}
          <div className="relative">
            <div className="relative rounded-3xl bg-white p-6 shadow-2xl ring-1 ring-gray-100 lg:p-8">
              <div className="relative h-96 overflow-hidden rounded-2xl bg-gradient-to-br from-[#10B981]/20 to-indigo-500/20 lg:h-[480px]">
                <img
                  src="https://plus.unsplash.com/premium_photo-1658506671316-0b293df7c72b?q=80&w=870&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                  alt="صيدلي استشاري"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />

                <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/60 via-black/20 to-transparent">
                  <div className="space-y-4 text-center text-white">
                    <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-[#10B981] shadow-lg ring-4 ring-white/20">
                      <Stethoscope className="h-12 w-12 text-white" />
                    </div>
                    <div className="space-y-1.5">
                      <h3 className="text-xl font-bold">د. ريان الحسيني</h3>
                      <p className="opacity-90">صيدلي استشاري</p>
                      <div className="flex justify-center">
                        <div className="flex text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 fill-current" />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* عناصر تفاعلية */}
            <div className="absolute -right-4 -top-4 rounded-xl bg-white p-4 shadow-lg ring-1 ring-gray-100 transition-transform duration-300 hover:-translate-y-1">
              <div className="flex items-center gap-2">
                <div className="h-3 w-3 animate-pulse rounded-full bg-green-500" />
                <span className="text-sm font-semibold text-gray-700">متاح الآن</span>
              </div>
            </div>

            <div className="absolute -bottom-4 -left-4 rounded-xl bg-white p-4 shadow-lg ring-1 ring-gray-100 transition-transform duration-300 hover:-translate-y-1">
              <div className="flex items-center gap-3">
                <div className="rounded-full bg-[#10B981]/10 p-2.5">
                  <Shield className="h-4 w-4 text-[#10B981]" />
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-800">100% آمن</div>
                  <div className="text-xs text-gray-500">معتمد من وزارة الصحة</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
